import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NearestPoiService } from '../nearest-poi.service';
import { MapBoxService } from '../../../../../mapbox/mapbox.service';
import { MatTooltip } from '@angular/material/tooltip';
import { pointsMinZoom } from '../../../../../../shared/util/consts';
import { ORI_LAYER } from '../../../../../mapbox/services/layer-store.service';
import { schoolsPoiLayers } from '../../education/education.service';
import { BreakpointObserverService } from '../../../../../../shared/services/breakpoint-observer.service';
import { LayersMenuService } from '../../layers-menu.service';

@Component({
  selector: 'app-nearest-poi-panel',
  standalone: true,
  imports: [CommonModule, MatTooltip],
  templateUrl: './poi-distance-block.component.html',
  styleUrls: [
    './poi-distance-block.component.scss',
    '../../../../../../app.component.scss',
  ],
  providers: [NearestPoiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NearestPoiPanelComponent implements OnInit, OnDestroy {
  public nearestPoiService = inject(NearestPoiService);
  public mapboxService = inject(MapBoxService);
  private breakpointObserver = inject(BreakpointObserverService);
  private menuService = inject(LayersMenuService);

  @Input() isSchool = false;
  @Input() isOri = false; // police

  public label!: string;
  public hasVisiblePois = false;
  private layerIds: string[] = [];

  ngOnInit() {
    this.nearestPoiService.setMode(this.isSchool ? 'school' : 'ori');

    if (this.isOri) {
      this.label = 'nearest police station';
    } else if (this.isSchool) {
      this.label = 'nearest school';
    }

    this.layerIds = this.isSchool ? schoolsPoiLayers : [ORI_LAYER];

    // Trigger initial fetch
    this.onMoveEnd();
    // Subscribe to map moves for this panel instance
    this.mapboxService.map.on('moveend', this.onMoveEnd);
    // Check visible pois on idle too to update when poi loaded
    this.mapboxService.map.on('idle', this.checkVisiblePois);
  }

  ngOnDestroy() {
    this.mapboxService.map.off('moveend', this.onMoveEnd);
    this.mapboxService.map.off('idle', this.checkVisiblePois);
  }

  private onMoveEnd = () => {
    const c = this.mapboxService.map.getCenter();
    this.nearestPoiService.setCenter(c);
    this.checkVisiblePois();
  };

  private checkVisiblePois = () => {
    const map = this.mapboxService.map;

    if (!map) {
      this.hasVisiblePois = false;
      return;
    }

    if (map.getZoom() < pointsMinZoom) {
      this.hasVisiblePois = false;
      return;
    }

    const layers = this.layerIds;

    if (layers.length === 0) {
      this.hasVisiblePois = false;
      return;
    }

    const canvas = map.getCanvas();
    const isMobile = this.breakpointObserver.isMobile.value;

    const featuress = map.queryRenderedFeatures(
      // Add threshold to compensate right menu
      [
        [0, 0],
        [canvas.width - (isMobile ? 0 : 500), canvas.height],
      ],
      { layers }
    );
    this.hasVisiblePois = featuress.length > 0;
  };

  public handleGoTo(lat: number, lng: number) {
    if (this.breakpointObserver.isMobile.value) {
      this.menuService.isMenuOpened = false;
    }

    const curZoom = this.mapboxService.map.getZoom();
    this.mapboxService.map.flyTo({
      center: [lng, lat],
      zoom: curZoom >= pointsMinZoom ? curZoom : pointsMinZoom,
    });
  }
}
